import { z } from 'zod';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';

/**
 * The Merge-based authentication method for writing back hiring readiness scores to Lever.
 */
export const WriteBackToLeverMergeAuthMethod = z.object({
  type: z.literal('merge'),

  /**
   * The data set ID that refers to the Merge ATS data set.
   */
  dataSetId: z.string().optional(),
});

export type WriteBackToLeverMergeAuthMethod = z.infer<
  typeof WriteBackToLeverMergeAuthMethod
>;

/**
 * The API authentication method for writing back hiring readiness scores to Lever.
 */
export const WriteBackToLeverApiAuthMethod = z.object({
  type: z.literal('api'),

  /**
   * The API key secret reference.
   */
  apiKeySecretReference: SecretReference.optional(),
});

export type WriteBackToLeverApiAuthMethod = z.infer<
  typeof WriteBackToLeverApiAuthMethod
>;

export const WriteBackToLeverConnectionMethod = z.object({
  type: z.literal('connection'),
});

export type WriteBackToLeverConnectionMethod = z.infer<
  typeof WriteBackToLeverConnectionMethod
>;

/**
 * The authentication method for writing back hiring readiness scores to Lever.
 */
export const WriteBackToLeverAuthMethod = z.union([
  WriteBackToLeverMergeAuthMethod,
  WriteBackToLeverApiAuthMethod,
  WriteBackToLeverConnectionMethod,
]);

export type WriteBackToLeverAuthMethod = z.infer<
  typeof WriteBackToLeverAuthMethod
>;

/**
 * The "note" place to write hiring readiness score results to Lever.
 */
export const WriteBackToLeverNotePlaceToWrite = z.object({
  type: z.literal('note'),
});

export type WriteBackToLeverNotePlaceToWrite = z.infer<
  typeof WriteBackToLeverNotePlaceToWrite
>;

export const WriteBackToLeverTagPlaceToWrite = z.object({
  type: z.literal('tag'),
});

export type WriteBackToLeverTagPlaceToWrite = z.infer<
  typeof WriteBackToLeverTagPlaceToWrite
>;

/**
 * The possible places to write for Lever.
 * This will be expanded in the future into a union.
 */
export const WriteBackToLeverPlaceToWrite = z.union([
  WriteBackToLeverNotePlaceToWrite,
  WriteBackToLeverTagPlaceToWrite,
]);

export type WriteBackToLeverPlaceToWrite = z.infer<
  typeof WriteBackToLeverPlaceToWrite
>;

/**
 * The strategy for writing back hiring readiness scores to Lever.
 */
export const WriteBackToLeverStrategy = z.object({
  type: z.literal('lever'),

  /**
   * The authentication method.
   */
  authMethod: WriteBackToLeverAuthMethod.optional(),

  /**
   * The places to write.
   */
  placesToWrite: z.array(WriteBackToLeverPlaceToWrite).optional(),
});

export type WriteBackToLeverStrategy = z.infer<typeof WriteBackToLeverStrategy>;
